"use client";

import React, { useMemo } from "react";
import Flex from "../../Flex";
import { AnyObject, Asset, Kit, Portal, Product, Space, User } from "../../../models";
import { buildURL } from "../../../helpers/buildUrl";
import ImageView from "../../ImageView";
import Icon from "../../Icon";
import styled from "styled-components";
import Button from "../../Button";
import LinkWrapper from "../../LinkWrapper";
import Text from "../../Text";
import ActivityIndicator from "../../ActivityIndicator";
import { getColor, getColorBasedOnLightness } from "../../utils";
import { ElementBackgroundProps } from "../../ComponentProps";

const ExpandButton = styled.button<{ rowSize: string }>`
  position: absolute;
  z-index: 5;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 4px;

  .asset_row_hover {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  ${props =>
    props.rowSize === "small"
      ? `height: 100%; width: 100%; bottom: 0; right: 0;`
      : props.rowSize === "medium"
        ? `bottom: 4px; right: 4px; height: 28px; width: 28px;`
        : `bottom: 8px; right: 8px; height: 28px; width: 28px;`}
`;

const CellWrapper = styled(Flex)`
  align-items: center;
  overflow: hidden;
  &:hover {
    .asset_row_hover {
      opacity: 1;
    }
  }
`;

export const TableCellText = styled(Text)<{ indent?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
  min-width: 0;
  padding-left: ${props => (props.indent ? "40px" : "0")};
  * {
    user-select: none;
  }
`;

function TableCell({
  data,
  space,
  icon,
  imageBackground,
  rowSize = "medium",
  insights = true,
  isClickable = true,
}: {
  data: AnyObject;
  space: Space;
  icon?: string;
  imageBackground?: ElementBackgroundProps;
  rowSize?: string;
  insights?: boolean;
  isClickable?: boolean;
}) {
  const dataType = useMemo(() => {
    if ("kitId" in data) return "kit";
    if ("avatar" in data) return "user";
    if ("counts" in data && "kits" in (data as Portal).counts) return "portal";
    if ("productTypeUuid" in data || "product_type_uuid" in data) return "product";
    return "asset";
  }, [data]);

  const asset = dataType === "asset" && (data as Asset);
  const user = dataType === "user" && (data as User);
  const kit = dataType === "kit" && (data as Kit);
  const portal = dataType === "portal" && (data as Portal);
  const product = dataType === "product" && (data as Product);

  const isActive = !["deleted", "trashed"].includes((data as AnyObject).status as string);
  const _icon =
    icon === undefined
      ? {
          kit: "content.kit",
          user: "info.globe",
          portal: "content.portal",
          product: "content.bag",
          asset: "content.file",
        }[dataType]
      : icon;
  const backgroundColor = getColor(imageBackground?.background ?? "grayLighter");
  const iconColor = getColorBasedOnLightness(backgroundColor, "grayDarker", "grayLighter");

  const getUrl = () => {
    switch (dataType) {
      case "asset":
        return buildURL(`/library/a/${asset.id}`, { space });
      case "kit":
        return buildURL(`/k/${kit.kitId}`, { space });
      case "portal":
        return buildURL(`/p/${portal.id}`, { space });
      case "product":
        return buildURL(`/products/p/${product.uuid}`, { space });
      default:
        return undefined;
    }
  };

  const getImageSource = () => {
    if (asset) {
      const url = asset?.thumbnails?.[292];
      if (!url) return null;
      return `${url}&p=${asset?.meta?.assetProcessing}`;
    }
    if (product) {
      return product.previewAsset?.thumbnails?.[292] ?? null;
    }
    return (
      portal?.images?.cover ?? (kit?.images?.cover || kit?.["kitCover"] || user?.avatar || null)
    );
  };

  const imageSizes = {
    small: 30,
    medium: 64,
    large: 120,
  };

  const imageSize = imageSizes[rowSize];

  const renderThumbnail = () => {
    const imageView = (
      <ImageView
        width={user ? "40" : imageSize}
        height={user ? "40" : imageSize}
        borderRadius={user ? "50%" : "default"}
        iconSize={24}
        icon={_icon}
        iconFill={iconColor}
        src={getImageSource()}
        {...imageBackground}
        cover={!asset}
      />
    );
    if (asset?.meta?.assetProcessing === "complete") {
      return imageView;
    }
    if (asset?.meta?.assetProcessing === "error") {
      return (
        <ImageView
          width={imageSize}
          height={imageSize}
          icon={"info.error"}
          iconSize={32}
          iconFill="error"
        />
      );
    }
    if (asset?.meta?.assetProcessing === "processing") {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          width={imageSize}
          height={imageSize}
          background="grayLighter">
          <ActivityIndicator size="small" />
        </Flex>
      );
    }
    return imageView;
  };

  return (
    <CellWrapper pl={insights && "40px"} data-assetid={asset?.id}>
      {/* thumbnail / avatar */}
      <LinkWrapper
        link={isActive && isClickable ? getUrl() : undefined}
        target={!asset && !product && "_blank"}>
        <div style={{ position: "relative" }}>
          {isActive && !user && isClickable && (
            <ExpandButton rowSize={rowSize} className="asset_row_hover">
              <Icon
                iconId={asset || product ? "action.maximize" : "action.external-link"}
                size={16}
                fill={space?.theme.primaryColor}
              />
            </ExpandButton>
          )}
          {renderThumbnail()}
        </div>
      </LinkWrapper>
      {/* text */}
      {(asset || product) && isActive && isClickable ? (
        <Button
          buttonStyle="tertiary"
          link={getUrl()}
          text={
            data.name || asset?.meta?.font?.displayName || asset?.colors?.[0]?.name || "Unnamed"
          }
          truncate
          ml="m"
        />
      ) : (
        <TableCellText ml={"m"}>
          {data.name ||
            asset?.meta?.font?.displayName ||
            asset?.colors?.[0]?.name ||
            (data as AnyObject).kitName ||
            "Unnamed"}
        </TableCellText>
      )}
    </CellWrapper>
  );
}

export default React.memo(TableCell) as typeof TableCell;
